import { Injectable, ApplicationRef, createComponent, Type, ComponentRef, Injector, InjectionToken } from '@angular/core';
import { Observable, Subject } from 'rxjs';

export interface ModalConfig<T = any> {
  component: Type<T>;
  data?: Partial<T>;
}

export interface ModalRef<T = any> {
  componentRef: ComponentRef<T>;
  onClose: Observable<void>;
  zIndex: number;
}

export const MODAL_REF = new InjectionToken<ModalRef>('MODAL_REF');

@Injectable({
  providedIn: 'root'
})
export class ModalService {
  private modalStack: Array<{
    modalRef: ModalRef<any>;
    closeSubject: Subject<void>;
  }> = [];
  private baseZIndex = 1000;

  constructor(
    private appRef: ApplicationRef,
    private injector: Injector
  ) {}

  open<T>(component: Type<T>, config: { data?: Partial<T> } = {}): ModalRef<T> {
    const closeSubject = new Subject<void>();
    const zIndex = this.baseZIndex + this.modalStack.length;

    const modalRef: ModalRef<T> = {
      componentRef: null!,
      onClose: closeSubject.asObservable(),
      zIndex
    };

    const componentRef = createComponent(component, {
      environmentInjector: this.appRef.injector,
      elementInjector: Injector.create({
        providers: [
          { provide: MODAL_REF, useValue: modalRef }
        ],
        parent: this.injector
      })
    });

    if (config.data) {
      Object.assign(componentRef.instance as object, config.data);
    }

    modalRef.componentRef = componentRef;
    componentRef.location.nativeElement.style.zIndex = zIndex;

    document.body.appendChild(componentRef.location.nativeElement);
    this.modalStack.push({ modalRef, closeSubject });
    this.appRef.attachView(componentRef.hostView);

    componentRef.changeDetectorRef.detectChanges();

    return modalRef;
  }

  close(modalRef: ModalRef<any>) {
    const index = this.modalStack.findIndex(item => item.modalRef === modalRef);
    if (index > -1) {
      const { closeSubject } = this.modalStack[index];
      this.modalStack.splice(index, 1);

      const element = modalRef.componentRef.location.nativeElement;
      if (element && element.parentNode) {
        element.parentNode.removeChild(element);
      }

      modalRef.componentRef.destroy();
      closeSubject.next();
      closeSubject.complete();
    }
  }

  closeAll() {
    [...this.modalStack].reverse().forEach(({ modalRef }) => this.close(modalRef));
    this.modalStack = [];

    document.querySelectorAll('.modal-backdrop').forEach(backdrop => {
      backdrop.parentNode?.removeChild(backdrop);
    });
  }

  activeModal() {
    return this.modalStack[this.modalStack.length - 1]?.modalRef;
  }
}
